import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { TennisBall } from "@phosphor-icons/react";

/** Live scoreboard widget for a specific court. Shows the active match if one is live on this court at the club.
 * Polls every 10s while mounted.
 *
 * Props:
 *   clubId, courtName
 */
export default function CourtScoreboardWidget({ clubId, courtName }) {
  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!clubId || !courtName) return;
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get(`/matches?status=en_curso&club_id=${clubId}&court_name=${encodeURIComponent(courtName)}`);
        if (!alive) return;
        // Backend may not filter by court_name; do client-side filter as a safety net.
        const live = (data || []).filter((m) => (m.status === "en_curso" || m.status === "pendiente_validacion") && (m.court_name === courtName || !m.court_name));
        const scoped = live.filter((m) => m.club_id === clubId && m.court_name === courtName);
        const arr = scoped.length ? scoped : live.filter((m) => m.club_id === clubId);
        setMatch(arr[0] || null);
      } catch {
        if (alive) setMatch(null);
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    const t = setInterval(load, 10000);
    return () => { alive = false; clearInterval(t); };
  }, [clubId, courtName]);

  if (loading) {
    return <div className="bg-[#0A0A0A] border border-white/10 px-3 py-2 text-[10px] uppercase tracking-widest text-gray-500" data-testid={`scoreboard-${courtName}-loading`}>Buscando partido…</div>;
  }
  if (!match) {
    return (
      <div className="bg-[#0A0A0A] border border-white/10 px-3 py-2 flex items-center gap-2" data-testid={`scoreboard-${courtName}-empty`}>
        <TennisBall size={14} className="text-gray-600" />
        <span className="text-[10px] uppercase tracking-widest text-gray-500">Sin partido en curso</span>
      </div>
    );
  }

  const sets = match.sets || [];
  const cs = match.current_set_games || { pair1: 0, pair2: 0 };
  return (
    <Link
      to={`/dashboard/marcador/${match.id}`}
      className="bg-gradient-to-r from-[#0A0A0A] to-[#1A1A1A] border-2 border-red-500/40 px-3 py-2 hover:border-red-500/70 transition-colors block"
      data-testid={`scoreboard-${courtName}`}
    >
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <span className="flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 bg-red-500 animate-pulse rounded-full" />
          <span className="text-[9px] uppercase tracking-widest text-red-400 font-bold">EN VIVO</span>
        </span>
        {match.category && <Badge variant="outline" className="bg-[#CCFF00]/10 text-[#CCFF00] border-[#CCFF00]/30 text-[8px]">Cat. {match.category}</Badge>}
      </div>
      <div className="grid grid-cols-[1fr_auto] gap-2 items-center">
        <div className="min-w-0">
          <p className={`text-[11px] truncate ${match.winner_pair === 1 ? "text-[#CCFF00] font-bold" : "text-gray-300"}`}>{match.pair1?.p1_name} / {match.pair1?.p2_name}</p>
          <p className={`text-[11px] truncate ${match.winner_pair === 2 ? "text-[#CCFF00] font-bold" : "text-gray-300"}`}>{match.pair2?.p1_name} / {match.pair2?.p2_name}</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex flex-col items-end">
            {sets.map((s, i) => <span key={i} className={`font-['Barlow_Condensed'] font-black text-base leading-none ${s.winner === 1 ? "text-[#CCFF00]" : "text-gray-600"}`}>{s.pair1_games}</span>)}
            <span className="font-['Barlow_Condensed'] font-black text-base leading-none text-orange-400">{cs.pair1}</span>
          </div>
          <div className="flex flex-col items-end">
            {sets.map((s, i) => <span key={i} className={`font-['Barlow_Condensed'] font-black text-base leading-none ${s.winner === 2 ? "text-[#CCFF00]" : "text-gray-600"}`}>{s.pair2_games}</span>)}
            <span className="font-['Barlow_Condensed'] font-black text-base leading-none text-orange-400">{cs.pair2}</span>
          </div>
        </div>
      </div>
    </Link>
  );
}
